import type { TFunction } from "i18next"
import { Link } from "react-router-dom"

import { ClipboardButton } from "@/components/commons/clipboard-button"
import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/utils"
import { DateHelper } from "@/utils"

import { WebhookDropdownAction } from "./dropdown-action"
import { EventsList } from "./events-list"
export interface WebhooksTableColumnItem {
  key: string
  visible: boolean
  header: {
    content: string
    className: string
  }
  cell: (item: WebhookConfigurationType) => {
    content: React.ReactNode
    className: string
  }
  mobile: (item: WebhookConfigurationType) => {
    label: string
    content: React.ReactNode
    className: string
  }
}
type CreateWebhooksTableDataParams = {
  columnVisibility: Record<string, boolean>
  t: TFunction
}
export const WEBHOOKS_DEFAULT_VISIBILITY: Record<
  string,
  boolean
> = {
  id: true,
  description: true,
  url: true,
  maxRetry: true,
  events: true,
  active: true,
  secretKey: false,
  blockManyErrors: false,
  createdAt: true,
  updatedAt: false,
  actions: true,
}
const StatusBadge = ({
  active,
  t,
}: {
  active: boolean
  t: TFunction
}) => (
  <Badge
    variant="outline"
    className="flex w-fit items-center gap-2 rounded-full"
  >
    <div
      className={cn(
        "h-2 w-2 rounded-full",
        active ? "bg-green-500" : "bg-red-500"
      )}
    />
    {active
      ? t("webhooks.table.active")
      : t("webhooks.table.inactive")}
  </Badge>
)
const BooleanBadge = ({
  value,
  t,
}: {
  value: boolean
  t: TFunction
}) => (
  <Badge
    variant="outline"
    className="flex w-fit items-center gap-2 rounded-full"
  >
    <div
      className={cn(
        "h-2 w-2 rounded-full",
        value ? "bg-green-500" : "bg-red-500"
      )}
    />
    {value ? t("common.yes") : t("common.no")}
  </Badge>
)
export const createWebhooksTableData = ({
  columnVisibility,
  t,
}: CreateWebhooksTableDataParams): WebhooksTableColumnItem[] => {
  return [
    {
      key: "id",
      visible: columnVisibility["id"] ?? true,
      header: {
        content: "ID",
        className: "hidden lg:table-cell",
      },
      cell: (item) => ({
        content: (
          <div className="flex items-center gap-2">
            <span
              className="max-w-[120px] truncate text-sm"
              title={item.id}
            >
              {item.id}
            </span>
            <ClipboardButton
              title={t("common.copy")}
              text={item.id}
              className="shrink-0"
            />
          </div>
        ),
        className: cn("hidden lg:table-cell"),
      }),
      mobile: (item) => ({
        label: "ID",
        content: (
          <div className="flex items-center gap-1">
            <span
              className="truncate text-xs"
              title={item.id}
            >
              {item.id.slice(-12)}
            </span>
            <ClipboardButton
              title={t("common.copy")}
              text={item.id}
              className="h-5 w-5"
            />
          </div>
        ),
        className: "lg:hidden",
      }),
    },
    {
      key: "description",
      visible: columnVisibility["description"] ?? true,
      header: {
        content: t("webhooks.table.description"),
        className: "hidden lg:table-cell",
      },
      cell: (item) => ({
        content: (
          <span
            className="max-w-[150px] truncate"
            title={item.description}
          >
            {item.description}
          </span>
        ),
        className: cn("hidden lg:table-cell"),
      }),
      mobile: (item) => ({
        label: t("webhooks.table.description"),
        content: (
          <span className="truncate text-xs">
            {item.description}
          </span>
        ),
        className: "lg:hidden col-span-2",
      }),
    },
    {
      key: "url",
      visible: columnVisibility["url"] ?? true,
      header: {
        content: "URL",
        className: "hidden lg:table-cell",
      },
      cell: (item) => ({
        content: (
          <div className="flex items-center gap-2">
            <Link
              to={item.url}
              target="_blank"
              className="text-primary underline"
            >
              Link
            </Link>
            <ClipboardButton
              title={t("common.copy")}
              text={item.url}
              className="shrink-0"
            />
          </div>
        ),
        className: cn("hidden lg:table-cell"),
      }),
      mobile: (item) => ({
        label: "URL",
        content: (
          <div className="flex items-center gap-1">
            <Link
              to={item.url}
              target="_blank"
              className="text-xs text-primary underline"
            >
              {t("webhooks.table.open")}
            </Link>
            <ClipboardButton
              title={t("common.copy")}
              text={item.url}
              className="h-5 w-5"
            />
          </div>
        ),
        className: "lg:hidden",
      }),
    },
    {
      key: "maxRetry",
      visible: columnVisibility["maxRetry"] ?? true,
      header: {
        content: t("webhooks.table.retries"),
        className: "hidden lg:table-cell text-center",
      },
      cell: (item) => ({
        content: (
          <span className="text-sm">{item.maxRetry}</span>
        ),
        className: cn("hidden lg:table-cell text-center"),
      }),
      mobile: (item) => ({
        label: t("webhooks.table.retries"),
        content: (
          <span className="text-xs">{item.maxRetry}</span>
        ),
        className: "lg:hidden",
      }),
    },
    {
      key: "events",
      visible: columnVisibility["events"] ?? true,
      header: {
        content: t("webhooks.table.events"),
        className: "hidden lg:table-cell",
      },
      cell: (item) => ({
        content: <EventsList events={item.events} />,
        className: cn("hidden lg:table-cell"),
      }),
      mobile: (item) => ({
        label: t("webhooks.table.events"),
        content: <EventsList events={item.events} />,
        className: "lg:hidden",
      }),
    },
    {
      key: "active",
      visible: columnVisibility["active"] ?? true,
      header: {
        content: t("common.status"),
        className: "hidden lg:table-cell",
      },
      cell: (item) => ({
        content: <StatusBadge active={item.active} t={t} />,
        className: cn("hidden lg:table-cell"),
      }),
      mobile: (item) => ({
        label: t("common.status"),
        content: <StatusBadge active={item.active} t={t} />,
        className: "lg:hidden",
      }),
    },
    {
      key: "secretKey",
      visible: columnVisibility["secretKey"] ?? false,
      header: {
        content: t("webhooks.table.key"),
        className: "hidden lg:table-cell",
      },
      cell: (item) => ({
        content: (
          <span className="text-sm">{item.secretKey}</span>
        ),
        className: cn("hidden lg:table-cell"),
      }),
      mobile: (item) => ({
        label: t("webhooks.table.key"),
        content: (
          <span className="text-xs">{item.secretKey}</span>
        ),
        className: "lg:hidden",
      }),
    },
    {
      key: "blockManyErrors",
      visible: columnVisibility["blockManyErrors"] ?? false,
      header: {
        content: t("webhooks.table.blockFailures"),
        className: "hidden lg:table-cell",
      },
      cell: (item) => ({
        content: (
          <BooleanBadge
            value={item.blockManyErrors}
            t={t}
          />
        ),
        className: cn("hidden lg:table-cell"),
      }),
      mobile: (item) => ({
        label: t("webhooks.table.blockFailures"),
        content: (
          <BooleanBadge
            value={item.blockManyErrors}
            t={t}
          />
        ),
        className: "lg:hidden",
      }),
    },
    {
      key: "createdAt",
      visible: columnVisibility["createdAt"] ?? true,
      header: {
        content: t("webhooks.table.createdAt"),
        className: "hidden lg:table-cell",
      },
      cell: (item) => {
        const createdAt = item.createdAt
        return {
          content: (
            <span className="whitespace-nowrap text-xs">
              {createdAt
                ? DateHelper.formatDate(
                    createdAt,
                    "dd/MM/yy HH:mm"
                  )
                : "-"}
            </span>
          ),
          className: cn("hidden lg:table-cell"),
        }
      },
      mobile: (item) => {
        const createdAt = item.createdAt
        return {
          label: t("webhooks.table.createdAt"),
          content: (
            <span className="text-xs">
              {createdAt
                ? DateHelper.formatDate(
                    createdAt,
                    "dd/MM/yy HH:mm"
                  )
                : "-"}
            </span>
          ),
          className: "lg:hidden",
        }
      },
    },
    {
      key: "updatedAt",
      visible: columnVisibility["updatedAt"] ?? false,
      header: {
        content: t("webhooks.table.updatedAt"),
        className: "hidden lg:table-cell",
      },
      cell: (item) => ({
        content: (
          <span className="whitespace-nowrap text-xs">
            {DateHelper.formatDate(
              item.updatedAt,
              "dd/MM/yy HH:mm"
            )}
          </span>
        ),
        className: cn("hidden lg:table-cell"),
      }),
      mobile: (item) => ({
        label: t("webhooks.table.updatedAt"),
        content: (
          <span className="text-xs">
            {DateHelper.formatDate(
              item.updatedAt,
              "dd/MM/yy HH:mm"
            )}
          </span>
        ),
        className: "lg:hidden",
      }),
    },
    {
      key: "actions",
      visible: columnVisibility["actions"] ?? true,
      header: {
        content: t("common.actions"),
        className: "hidden lg:table-cell text-right",
      },
      cell: (item) => ({
        content: <WebhookDropdownAction webhook={item} />,
        className: cn("hidden lg:table-cell text-right"),
      }),
      mobile: (item) => ({
        label: t("common.actions"),
        content: <WebhookDropdownAction webhook={item} />,
        className: "lg:hidden col-span-2",
      }),
    },
  ]
}
